/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  Box,
  IconButton,
  Typography,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { HIRO_LINE_TYPES, HiroLineType } from '../../../../types';
import { useHiroCtx } from '../../HiroProvider';
import { SGTooltip } from '../../../core';
import { useState, useCallback } from 'react';

import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import OhlcIcon from 'icons/OHLC';
import { useTheme } from '@mui/material/styles';
const LINE_TYPE_ICON: Record<HiroLineType, React.ReactNode> = {
  Line: <ShowChartIcon sx={{ fontSize: 16 }} />,
  Candle: <CandlestickChartIcon sx={{ fontSize: 16 }} />,
  OHLC: <OhlcIcon />,
};

export const ChartTypeButton = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { hiroLineType, priceLineType, update } = useHiroCtx();

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);

  const typeSectionSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
  };

  const sectionLabelSx = {
    fontSize: 11,
    fontWeight: 600,
    fontFamily: 'Satoshi Complete',
    color: 'text.secondary',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  };

  const typeButtonSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    px: '10px',
    py: '7px',
    borderRadius: '8px',
    cursor: 'pointer',
    border: `1px solid ${active ? theme.palette.primary.main : 'rgba(255,255,255,0.08)'}`,
    bgcolor: active ? 'rgba(24,189,172,0.1)' : 'transparent',
    color: active ? theme.palette.primary.main : 'text.secondary',
    transition: 'all 0.15s',
    '&:hover': {
      bgcolor: active ? 'rgba(24,189,172,0.14)' : 'rgba(255,255,255,0.04)',
      borderColor: active
        ? theme.palette.primary.main
        : 'rgba(255,255,255,0.18)',
      color: active ? theme.palette.primary.main : 'text.primary',
    },
  });

  return (
    <>
      <SGTooltip title="Chart type" placement="top">
        <IconButton
          onClick={handleClick}
          size="medium"
          sx={{
            color: open
              ? theme.palette.primary.main
              : theme.palette.primary.main,
          }}
        >
          <Typography
            sx={{ color: 'inherit', display: 'flex', alignItems: 'center' }}
          >
            <CandlestickChartIcon />
          </Typography>
        </IconButton>
      </SGTooltip>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: 1_298 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Box
            sx={{
              mt: '8px',
              width: '200px',
              backgroundColor: theme.palette.background.paper,
              borderRadius: '14px',
              boxShadow:
                '0 8px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                px: '14px',
                py: '10px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                background:
                  'linear-gradient(135deg, rgba(24,189,172,0.07) 0%, transparent 60%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  fontFamily: 'Satoshi Complete',
                  color: 'text.primary',
                }}
              >
                Chart Type
              </Typography>
            </Box>

            {/* Body */}
            <Box
              sx={{
                p: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '14px',
              }}
            >
              {/* HIRO section */}
              <Box sx={typeSectionSx}>
                <Typography sx={sectionLabelSx}>HIRO</Typography>
                {HIRO_LINE_TYPES.map((t) => (
                  <Box
                    key={t}
                    onClick={() =>
                      update({
                        hiroLineType: t,
                      })
                    }
                    sx={typeButtonSx(hiroLineType === t)}
                  >
                    {LINE_TYPE_ICON[t]}
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'SF Pro Display',
                        fontWeight: hiroLineType === t ? 600 : 400,
                        color: 'inherit',
                      }}
                    >
                      {t}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.06)' }} />

              {/* Price section */}
              <Box sx={typeSectionSx}>
                <Typography sx={sectionLabelSx}>Price</Typography>
                {HIRO_LINE_TYPES.map((t) => (
                  <Box
                    key={t}
                    onClick={() =>
                      update({
                        priceLineType: t,
                      })
                    }
                    sx={typeButtonSx(priceLineType === t)}
                  >
                    {LINE_TYPE_ICON[t]}
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontFamily: 'SF Pro Display',
                        fontWeight: priceLineType === t ? 600 : 400,
                        color: 'inherit',
                      }}
                    >
                      {t}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
